import { useEffect, useState } from 'react';
import { learningApi } from '../../../api/learning.js';

/**
 * Learning paths — ordered sequences of courses. Create a path, then add
 * courses to it.
 */
export default function Paths() {
  const [paths, setPaths] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  function refresh() {
    setLoading(true);
    Promise.all([learningApi.listPaths(), learningApi.listCourses()])
      .then(([p, c]) => {
        setPaths(p?.items || []);
        setCourses(c?.items || []);
      })
      .catch((e) => setError(e.response?.data?.message || 'Failed to load paths'))
      .finally(() => setLoading(false));
  }
  useEffect(refresh, []);

  async function createPath(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await learningApi.createPath({ name: fd.get('name'), description: fd.get('description') || null });
      e.target.reset();
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create path');
    }
  }

  async function openPath(id) {
    try {
      setSelected(await learningApi.getPath(id));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to open path');
    }
  }

  async function addCourse(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await learningApi.addPathCourse(selected.path.id, { courseId: fd.get('courseId') });
      await openPath(selected.path.id);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add course');
    }
  }

  return (
    <section data-testid="learning-paths-page" data-loading={loading ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Learning Paths</h1>
          <p className="muted">{paths.length} paths</p>
        </div>
      </header>

      {error && <div className="alert error" data-testid="learning-paths-error">{error}</div>}

      <form onSubmit={createPath} className="card form-card grid-form">
        <label>Name<input name="name" data-testid="learning-path-name-input" required /></label>
        <label>Description<input name="description" data-testid="learning-path-desc-input" /></label>
        <div className="full actions"><button className="btn primary" type="submit" data-testid="learning-path-submit">Create path</button></div>
      </form>

      <div className="card">
        <table className="data-table" data-testid="learning-path-list">
          <thead><tr><th>Name</th><th>Courses</th><th></th></tr></thead>
          <tbody>
            {paths.map((p) => (
              <tr key={p.id} data-testid={`learning-path-row-${p.id}`}>
                <td>{p.name}</td>
                <td>{p.course_count}</td>
                <td><button className="btn ghost" data-testid={`learning-path-open-${p.id}`} onClick={() => openPath(p.id)}>Open</button></td>
              </tr>
            ))}
            {!loading && paths.length === 0 && <tr><td colSpan={3} className="muted">No paths yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="card" data-testid="learning-path-detail">
          <h3>{selected.path.name}</h3>
          <ol>
            {selected.courses.map((c) => <li key={c.path_course_id}>{c.title}</li>)}
            {selected.courses.length === 0 && <li className="muted">No courses in this path.</li>}
          </ol>
          <form onSubmit={addCourse} className="inline-form">
            <select name="courseId" data-testid="learning-path-course-input" required defaultValue="">
              <option value="" disabled>— select course —</option>
              {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
            <button className="btn primary" type="submit" data-testid="learning-path-course-add">Add course</button>
          </form>
        </div>
      )}
    </section>
  );
}
